import { isParent } from "../tree/id.js";
import "../tree/node.js";
import { describeHandler } from "../inspector.js";
import { createDataHandler } from "./metadata.js";
import "./handler.js";
//#region src/handler/focus.ts
function createFocusHandler(options = {}) {
	const focusWhenEmpty = options.focusWhenEmpty ?? true;
	const focusHandler = (action, { node, next }) => {
		if (import.meta.env.FIVEWAY_INSPECTOR ?? import.meta.env.DEV) describeHandler(action, {
			name: "focus",
			focusWhenEmpty,
			direction: options.direction != null ? "custom" : "default"
		});
		if (action.kind !== "focus") return next();
		if (!node.children.some((c) => c.active)) {
			if (focusWhenEmpty) return node.id;
			return null;
		}
		const focusDirection = options.direction?.(action.direction) ?? null;
		if (focusDirection === null) {
			const initialChild = findInitialChild(node);
			if (initialChild !== null) {
				const childId = next(initialChild);
				if (childId !== null) return childId;
			}
		}
		if (focusDirection === "backwards") {
			for (let i = node.children.length - 1; i >= 0; i--) {
				const child = node.children[i];
				if (!child.active) continue;
				const nextId = next(child.id);
				if (nextId !== null) return nextId;
			}
			return null;
		}
		for (let i = 0; i < node.children.length; i++) {
			const child = node.children[i];
			if (!child.active) continue;
			const nextId = next(child.id);
			if (nextId !== null) return nextId;
		}
		return null;
	};
	return focusHandler;
}
/**
* Handler factory that creates a primitive {@link NavigationHandler} that is used to resolve {@link FocusAction}.
* Focus is resolved by walking children in the order determined by the `direction` option.
*
* @see {@link FocusHandlerOptions} for options
*
* @see {@link https://fiveway.dev/guide/built-in-handlers#focus-handler}
* @see {@link https://fiveway.dev/guide/handlers#composing-handlers} for more information about composing handlers.
* @see {@link NavigationHandler}
*/
const focusHandler = createFocusHandler;
/**
* Data handler that provides id for the preferred first child for initial focus.
*
* Requires subsequent handler such as `focusHandler` to use the provided initial child id.
*
* ```ts
* import { initialHandler, verticalHandler } from "@fiveway/core";
*
* const handler = verticalHandler.compose(initialHandler('item2'));
* ```
*/
const initialHandler = createDataHandler("initial");
/**
* Primitive handler that ensures the focus stays under the current node.
*
* This is a primitive handler and as such is meant to be used as part of a composed handler.
*
* ```ts
* import { verticalHandler, captureHandler } from "@fiveway/core";
*
* const handler = verticalHandler.compose(captureHandler);
* ```
*
* There are multiple ways to escape capture like explicit `focusNode()` or extending further with a custom handler.
*/
const captureHandler = (action, { node, next }) => {
	if (import.meta.env.FIVEWAY_INSPECTOR ?? import.meta.env.DEV) describeHandler(action, { name: "capture" });
	const id = next();
	if (id === null || !isParent(node.id, id)) return null;
	return id;
};
function findInitialChild(node) {
	const initialItem = initialHandler.query(node.tree, node.id);
	if (initialItem === null) return null;
	const initialId = `${node.id}/${initialItem}`;
	const child = node.children.find((c) => c.active && c.id === initialId);
	if (child == null) return null;
	return child.id;
}
//#endregion
export { captureHandler, focusHandler, initialHandler };

//# sourceMappingURL=focus.js.map